import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Grid, Card, CardContent, InputAdornment,
  TextField, CircularProgress, Alert, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { apiFetch } from '../api';

const formatDate = (dateStr) => {
  if (!dateStr) return '---';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

// Challan-wise reference display with tooltip
const ReferenceCell = ({ refs }) => {
  if (!refs || refs.length === 0) {
    return <span style={{ color: '#bbb' }}>---</span>;
  }

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      {refs.map((r, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', py: 0.4, borderBottom: i < refs.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none' }}>
          <strong>चलन #{r.challan_id}</strong> ({formatDate(r.date)}): {r.reference}
        </Typography>
      ))}
    </Box>
  );

  const firstRef = refs[0].reference;
  const moreCount = refs.length - 1;

  return (
    <Tooltip title={tooltipContent} arrow placement="top" componentsProps={{
      tooltip: { sx: { bgcolor: '#37474F', maxWidth: 300, fontSize: '0.78rem' } },
      arrow: { sx: { color: '#37474F' } }
    }}>
      <span style={{ cursor: 'help', borderBottom: '1px dashed #888', display: 'inline-block' }}>
        {firstRef}
        {moreCount > 0 && (
          <span style={{ color: '#6A1B9A', fontWeight: 'bold', marginLeft: 4 }}>
            +{moreCount} आणखी
          </span>
        )}
      </span>
    </Tooltip>
  );
};

const OutstandingReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchOutstanding = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch('/api/outstanding');
        if (res.ok) {
          setData(await res.json());
        } else {
          setError('डेटा लोड करताना त्रुटी आली.');
        }
      } catch (err) {
        setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
      } finally {
        setLoading(false);
      }
    };
    fetchOutstanding();
  }, []);

  const filteredData = data.filter(row =>
    row.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (row.mobile && row.mobile.includes(searchTerm))
  );

  const totalAmount = filteredData.reduce((sum, row) => sum + (row.outstanding_amount || 0), 0);

  return (
    <Box sx={{ p: 2 }}>

      {/* Print-only header */}
      <Box className="print-only" sx={{ mb: 2, textAlign: 'center', borderBottom: '2px solid black', pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>थकीत रिपोर्ट (Outstanding Report)</Typography>
        <Typography variant="body2">दिनांक: {formatDate(new Date().toISOString().split('T')[0])}</Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
          आउटस्टँडिंग रिपोर्ट (थकीत रक्कम आणि साहित्य)
        </Typography>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={() => window.print()}
          sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
        >
          PDF / Print
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#ffebee', borderLeft: '6px solid #d32f2f', borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                एकूण थकीत रक्कम (Total Outstanding)
              </Typography>
              <Typography variant="h4" sx={{ color: '#b71c1c', fontWeight: 'bold' }}>
                ₹ {totalAmount.toLocaleString('en-IN')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '6px solid #2e7d32', borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                एकूण थकीत ग्राहक
              </Typography>
              <Typography variant="h4" sx={{ color: '#1b5e20', fontWeight: 'bold' }}>
                {filteredData.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="no-print">
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
            ग्राहकांची यादी
          </Typography>
          <TextField
            size="small"
            placeholder="ग्राहक / मोबाईल शोधा..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#888' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#1B5E20' }} />
            <Typography sx={{ mt: 2, color: '#888' }}>डेटा लोड होत आहे...</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead sx={{ backgroundColor: '#1B5E20' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: 40 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>ग्राहक</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>मोबाईल</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>
                    रेफरन्स / ओळख
                    <Typography variant="caption" sx={{ display: 'block', color: '#a5d6a7', fontWeight: 'normal' }}>
                      (hover करा — चलन-wise)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">थकीत रक्कम (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>थकीत साहित्य</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>शेवटचा व्यवहार</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  <>
                    {filteredData.map((row, idx) => (
                      <TableRow key={row.customer_id} hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fbe7' }}>
                        <TableCell sx={{ color: '#aaa', fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#1B5E20' }}>{row.customer_name}</TableCell>
                        <TableCell sx={{ color: '#555' }}>{row.mobile || '---'}</TableCell>
                        <TableCell>
                          <ReferenceCell refs={row.challan_references} />
                        </TableCell>
                        <TableCell align="right"
                          sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: '1rem' }}>
                          ₹ {(row.outstanding_amount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ color: '#555', fontSize: '0.82rem' }}>
                          {row.pending_materials}
                        </TableCell>
                        <TableCell sx={{ color: '#777', fontSize: '0.82rem' }}>
                          {formatDate(row.last_transaction_date)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow sx={{ backgroundColor: '#ffebee', borderTop: '2px solid #ef9a9a' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#333' }}>
                        एकूण ({filteredData.length} ग्राहक)
                      </TableCell>
                      <TableCell align="right"
                        sx={{ fontWeight: 'bold', color: '#b71c1c', fontSize: '1.1rem' }}>
                        ₹ {totalAmount.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#888' }}>
                      कोणताही थकीत ग्राहक नाही.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default OutstandingReport;
